import { lazy, Suspense } from "react";
const SurvrepGoruntuleyici = lazy(() => import("@/components/survrep").then((m) => ({ default: m.SurvrepGoruntuleyici })));
import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useVeri, dosyaSec, dosyaYukle, tarih, tarihSaat } from "@/lib/veri";
import { Band, Bos, Buton, Cip, Etiket, Hata, Kutu, Kv, Sayfa, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { OlayDuzenle } from "@/components/olay-duzenle";
import { DonusumDurumu, siraIslemi, type DonusumBilgisi } from "./Mrb";

/**
 * MRB detayı: PDF kaydının yanında içerik excel'inin sayfaları. Tek model (D-017), input modeli
 * (MRB-input-yontemi.md): Identity · Known conflicts · C&K map · Documents · Component tree.
 */

interface Piece { id: string; sira: number; kod: string; sayfa: string | null; ad: string; duzenleyen: string | null; kimlik: string | null; kapsam: string | null; kapsamBeyani: string | null; komponent: string | null; masterdata: string | null; sinif: string | null }
interface Kimlik { id: string; sira: number; komponent: string; alan: string; deger: string | null; rev: string | null; kaynak: string | null; sinif: string | null; destek: number | null; toplam: number | null; eslesme: boolean; diger: string | null }
interface Karar { id: string; sira: number; no: string | null; komponent: string; alan: string | null; hat: string | null; tur: string; kanonik: string | null; detay: string | null; karar: string | null; kararVeren: string | null }
interface CkSatiri { id: string; sira: number; hat: string; komponent: string; alan: string; deger: string | null; rev: string | null; kaynak: string | null; destek: number | null; fizikiHat: string | null; diger: string | null }
interface Agac { id: string; sira: number; section: string; derinlik: number; komponent: string; pn: string | null; kimlik: string | null; kaynakPiece: string | null; masterdata: string | null; fmSatiri: string | null; notu: string | null }
interface Icerik {
  icerik: { id: string; dosya: string; kunye: string | null; assemblyPn: string | null; yukleyen: string; ts: string; dokumanId: string | null; model: string };
  pieceler: Piece[];
  agac: Agac[];
  kimlikler: Kimlik[];
  kararlar: Karar[];
  ckSatirlari: CkSatiri[];
  ozet: { model: string; piece: number; agac: number; kimlik: number; karar: number; ck: number; insanKarariBekleyen: number };
}
interface Detay {
  /** Dönüşüm alanları (D-060) da `mrb`de; içerik ayrı gelir. */
  mrb: { id: string; sn: string; dosya: string; durum: string; gelisTs: string; riserRn: string | null; pdfDokumanId: string | null; pdfAd: string | null } & Omit<DonusumBilgisi, "id" | "icerik">;
  icerik: Icerik | null;
  olaylar: { id: string; ts: string; kim: string; olay: string }[];
}

type Sekme = "agac" | "piece" | "kimlik" | "karar" | "ck";

/** "MRB'de kimlik kaydı YOK" bir eksiklik değil, DNV imzalı belgenin beyanıdır: kırmızı değil gri. */
const kimlikYok = (s: string | null) => Boolean(s && /kimlik kaydı YOK|SN yok|no (SN|ID) record in MRB/i.test(s));
/** Ağaç notundaki kimlik çelişkisi işaretleri; bunlar insan onayı bekler, göze çarpmalı. */
const celiskiNotu = (s: string | null) => Boolean(s && /⚠|ÇELİŞKİ|CONFLICT/i.test(s));
/** Metin araması: küçük/büyük harf ve Türkçe'ye duyarsız, kolonların tamamında gezer. */
const gecer = (ara: string, ...alanlar: (string | null)[]) => {
  const q = ara.trim().toLocaleLowerCase("tr");
  if (!q) return true;
  return alanlar.some((a) => (a ?? "").toLocaleLowerCase("tr").includes(q));
};

/** Ağaçtaki üst düzey section (0, 1, 2A, 3…); "1.2.1" satırı "1" grubuna girer. */
const ustSection = (section: string) => section.split(".")[0] ?? section;

/** Sekmelerin filtre çubuğu; hepsinde aynı yerleşim olsun diye tek bileşen. */
function FiltreCubugu({ children, gosterilen, toplam, temizle }: { children: ReactNode; gosterilen: number; toplam: number; temizle?: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
      {children}
      <span className="ml-auto text-xs text-ink-3 tnum">{gosterilen === toplam ? `${toplam} rows` : `${gosterilen} / ${toplam} rows`}</span>
      {temizle && gosterilen !== toplam && <Buton kucuk onClick={temizle}>{cevir("Clear")}</Buton>}
    </div>
  );
}

/** Künyenin ilk cümlesi dosya kimliğidir; kalanı şablonun genel açıklamasıdır, başlıkta yer kaplar. */
const kunyeKisa = (s: string | null) => (s ? s.split(". ")[0]! : null);

export function MrbDetay({ id }: { id: string }) {
  // Seçilmemişse ilk sekme (Identity) açılır.
  const [secilenSekme, setSekme] = useState<Sekme | null>(null);
  // Bütün sekmeler tamamen yüklü geldiği için istemci tarafında süzülür.
  const [agacAra, setAgacAra] = useState("");
  const [agacSection, setAgacSection] = useState("");
  const [pdf, setPdf] = useState<{ sayfa: number } | null>(null);
  const [pieceAra, setPieceAra] = useState("");
  const [pieceKapsam, setPieceKapsam] = useState("");
  const [kimlikAra, setKimlikAra] = useState("");
  const [kimlikKomponent, setKimlikKomponent] = useState("");
  const [sadeceEslesme, setSadeceEslesme] = useState(false);
  const [ckAra, setCkAra] = useState("");
  const [ckHat, setCkHat] = useState("");
  const { veri, hata, yenile } = useVeri<Detay>(`/mrb/${id}`);
  const [islem, setIslem] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [sonuc, setSonuc] = useState<{ ozet: Icerik["ozet"] & { assemblySn?: string | null }; uyarilar: string[]; degistirildi: boolean } | null>(null);
  const [pdfUyari, setPdfUyari] = useState<string[] | null>(null);
  const [siraMesgul, setSiraMesgul] = useState(false);

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  const { mrb, icerik } = veri;

  const icerikYukle = async () => {
    const f = await dosyaSec(".xlsx");
    if (!f) return;
    setIslem(null);
    setSonuc(null);
    setYukleniyor(true);
    try {
      const r = await dosyaYukle<{ ozet: Icerik["ozet"]; uyarilar: string[]; degistirildi: boolean }>(`/mrb/${id}/icerik`, f);
      setSonuc(r);
      setSekme(null);
      await yenile();
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Upload failed"));
    } finally {
      setYukleniyor(false);
    }
  };

  /** Dönüştürücü kuyruğu (D-060): sıraya ekle, sıradan çıkar ya da hatadan sonra yeniden dene. */
  const sira = async (i: "ekle" | "cikar") => {
    setIslem(null);
    setSiraMesgul(true);
    try {
      await siraIslemi(id, i);
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Operation failed"));
    } finally {
      setSiraMesgul(false);
      await yenile();
    }
  };

  /**
   * PDF'i degistirir (16.09 Bekir toplantisi): yanlis PDF referans hatasidir, veri hatasi degil.
   * Icerik excel'i ve ona dayanan denetimler yerinde kalir; veri yanlissa yol icerik excel'ini degistirmektir.
   */
  const pdfDegistir = async () => {
    const f = await dosyaSec(".pdf");
    if (!f) return;
    if (!window.confirm(cevir("Replace the MRB PDF? The content excel and the checks based on it stay as they are; the old PDF remains in the audit log."))) return;
    setIslem(null);
    setPdfUyari(null);
    setYukleniyor(true);
    try {
      const r = await dosyaYukle<{ uyarilar: string[] }>(`/mrb/${id}/pdf`, f);
      setPdfUyari(r.uyarilar.length ? r.uyarilar : null);
      await yenile();
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Upload failed"));
    } finally {
      setYukleniyor(false);
    }
  };

  /**
   * DOC_INDEX sayfa numarasi: MRB PDF'inin FIZIKSEL sayfasini acar.
   * Eskiden duz metindi — MrbKutuk'ta belge bagi olmadigi icin acilacak belgenin id'si
   * istemciye hic gitmiyordu. Belge yoksa eskisi gibi duz metin kalir.
   */
  const syf = (deger: string | null) => {
    const n = Number.parseInt((deger ?? "").trim(), 10);
    if (!deger) return <span className="tnum text-xs text-ink-3">—</span>;
    if (!Number.isFinite(n) || !mrb.pdfDokumanId) {
      return <span className="tnum text-xs" title={mrb.pdfDokumanId ? undefined : cevir("MRB PDF not in system; page cannot be opened")}>{deger}</span>;
    }
    return (
      <button type="button" className="tnum rounded bg-info-bg px-1.5 py-0.5 text-xs font-medium text-info hover:opacity-80" onClick={() => setPdf({ sayfa: n })}>
        {deger}
      </button>
    );
  };

  /**
   * "Kaynak" kolonu tek sayi degil, coklu referans tasir: "s3·s7·s8·s30·s31", "s19-22 (BOM)".
   * Metindeki her s<sayfa> belirtecini tiklanabilir cipe cevirir, aradaki metni oldugu gibi birakir.
   * Aralikta (s19-22) ilk sayfa acilir. Belge yoksa metin oldugu gibi kalir.
   * Guncel standart (rev3 §6) oneki "p" yazar ("p3·p7", "p19-22 (BOM)"); eski Turkce uretimler "s". Ikisi de taninir.
   */
  const kaynakSyf = (deger: string | null) => {
    if (!deger) return <span className="text-xs text-ink-3">—</span>;
    if (!mrb.pdfDokumanId) return <span className="text-xs text-ink-3" title={cevir("MRB PDF not in system; page cannot be opened")}>{deger}</span>;
    const parcalar: ReactNode[] = [];
    const desen = /\b[sp](\d+)(?:\s*[-–]\s*(\d+))?/gi;
    let son = 0;
    for (let m = desen.exec(deger); m; m = desen.exec(deger)) {
      if (m.index > son) parcalar.push(deger.slice(son, m.index));
      const ilk = Number.parseInt(m[1]!, 10);
      parcalar.push(
        <button key={`${m.index}-${m[0]}`} type="button" onClick={() => setPdf({ sayfa: ilk })}
          className="tnum mr-0.5 rounded bg-info-bg px-1 py-0.5 text-xs font-medium text-info hover:opacity-80">
          {m[0]}
        </button>,
      );
      son = m.index + m[0].length;
    }
    if (!parcalar.length) return <span className="text-xs text-ink-3">{deger}</span>;
    if (son < deger.length) parcalar.push(deger.slice(son));
    return <span className="text-xs text-ink-3">{parcalar}</span>;
  };

  /**
   * Input modelinin "Source (doc — page)" kolonu: "Trace Sheet (…) — p.34". Belge adı metin kalır,
   * sonundaki p.<sayfa> MRB PDF'inin o sayfasını açar.
   */
  const kaynakBelge = (deger: string | null) => {
    if (!deger) return <span className="text-xs text-ink-3">—</span>;
    const m = /^(.*?)\s*—\s*p\.?\s*(\d+)\s*$/.exec(deger);
    if (!m) return <span className="text-xs text-ink-2">{deger}</span>;
    return (
      <span className="text-xs text-ink-2">
        {m[1]}{" "}
        {mrb.pdfDokumanId ? (
          <button type="button" onClick={() => setPdf({ sayfa: Number(m[2]) })} className="tnum rounded bg-info-bg px-1.5 py-0.5 text-xs font-medium text-info hover:opacity-80">p.{m[2]}</button>
        ) : <span className="tnum">p.{m[2]}</span>}
      </span>
    );
  };

  const sekmeler: [Sekme, string, number][] = !icerik
    ? []
    : [["kimlik", cevir("Identity"), icerik.ozet.kimlik], ["karar", cevir("Known conflicts"), icerik.ozet.karar], ["ck", cevir("C&K map"), icerik.ozet.ck], ["piece", cevir("Documents"), icerik.ozet.piece], ["agac", cevir("Component tree"), icerik.ozet.agac]];
  const sekme: Sekme = secilenSekme ?? "kimlik";
  const sayaclar: [string, number][] = !icerik
    ? []
    : [[cevir("Identity"), icerik.ozet.kimlik], [cevir("Known conflicts"), icerik.ozet.karar], [cevir("C&K map"), icerik.ozet.ck], [cevir("Documents"), icerik.ozet.piece], [cevir("Tree row"), icerik.ozet.agac]];

  return (
    <Sayfa
      ust={<Link to="/mrb" className="normal-case tracking-normal text-orange">{cevir("← MRB inventory")}</Link>}
      baslik={<>MRB {mrb.sn}</>}
      alt={kunyeKisa(icerik?.icerik.kunye ?? null) ?? cevir("Structured form of MRB PDF (content excel) is linked to this record; the Disassembly old ID check reads its Identity and C&K map (and its COMPONENT_TREE page, if present).")}
      aksiyon={<div className="flex flex-wrap gap-2"><Buton kucuk onClick={() => api.indir("/mrb/sablon.xlsx")}>{cevir("Empty input template")}</Buton><Buton kucuk islem="mrb" tur={icerik ? "normal" : "birincil"} mesgul={yukleniyor} onClick={icerikYukle}>{yukleniyor ? cevir("Reading excel…") : icerik ? cevir("Change content excel") : cevir("Upload content excel")}</Buton></div>}
    >
      <Hata mesaj={islem} />
      {pdfUyari?.map((u, i) => <Band key={i} tip="not">{u}</Band>)}

      <Kutu>
        <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kv ad="Assembly SN"><b className="font-mono">{mrb.sn}</b></Kv>
          <Kv ad="PDF"><span className="inline-flex flex-wrap items-center gap-1"><Cip>{mrb.dosya}</Cip><Buton kucuk islem="mrb" mesgul={yukleniyor} onClick={pdfDegistir}>{cevir("Replace PDF")}</Buton></span></Kv>
          <Kv ad={cevir("Riser mapping")}>{mrb.riserRn ? <Etiket tone="ok">{mrb.riserRn}</Etiket> : <Etiket>{cevir("will be established in Disassembly")}</Etiket>}</Kv>
          <Kv ad={cevir("Arrival")}>{tarih(mrb.gelisTs)}</Kv>
        </div>
      </Kutu>

      {sonuc && (
        <Kutu baslik={sonuc.degistirildi ? cevir("Content changed") : cevir("Content uploaded")}>
          <p className="text-sm">{cevir`Input model read: ${sonuc.ozet.kimlik} identity · ${sonuc.ozet.karar} flagged conflict · ${sonuc.ozet.ck} C&K row · ${sonuc.ozet.piece} document · ${sonuc.ozet.agac} tree row.`}</p>
          {sonuc.uyarilar.map((u, i) => <Band key={i} tip="not" className="mt-2">{u}</Band>)}
        </Kutu>
      )}

      {!icerik ? (
        <Kutu baslik={cevir("No content excel")}>
          <p className="text-base text-ink-2">{cevir("Only PDF exists for this MRB. Before the content excel is uploaded, the old ID check in the Disassembly form cannot work for this riser.")}</p>
          <div className="mt-3 text-sm"><span className="mr-2 text-xs text-ink-3">{cevir("MRB converter")}</span><DonusumDurumu m={{ ...mrb, icerik: null }} mesgul={siraMesgul} islem={(i) => void sira(i)} /></div>
          <Band tip="bilgi" className="mt-3">{cevir("MRB content excel is the input model produced by the MRB engine, five pages:")} <b>IDENTITY · DECISIONS · CK_MAP · DOC_INDEX · COMPONENT_TREE</b>{cevir(". Row 1 of each page is description, row 2 is header. A file not matching the template (including old v3 files) is rejected and the reason is written. The disposition input is not uploaded here, but on the Inspection verification screen.")}</Band>
        </Kutu>
      ) : (
        <>
          <Kutu>
            <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-5">
              {sayaclar.map(([a, n]) => (
                <div key={a} className="rounded-md bg-surface-2 p-3"><div className="text-xs text-ink-3">{a}</div><div className="text-2xl font-semibold tnum">{n}</div></div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-ink-2">
              <span><span className="text-ink-3">{cevir("Source")}</span> {icerik.icerik.dosya}</span>
              {icerik.icerik.assemblyPn && <span><span className="text-ink-3">Assembly PN</span> <b className="font-mono">{icerik.icerik.assemblyPn}</b></span>}
              <span><span className="text-ink-3">{cevir("Uploaded by")}</span> {icerik.icerik.yukleyen} · {tarihSaat(icerik.icerik.ts)}</span>
              {icerik.icerik.dokumanId && <a className="font-semibold text-orange" href={`/api/dokuman/${icerik.icerik.dokumanId}`} target="_blank" rel="noreferrer">{cevir("Download Excel")}</a>}
            </div>
            {icerik.ozet.karar > 0 && (
              <Band tip="bilgi" className="mt-3">{cevir`${icerik.ozet.karar} conflicts flagged by the converter (Known conflicts tab). Canonical values are already chosen by rule; conflicts are kept as evidence for when a part ID does not match.`}</Band>
            )}
          </Kutu>

          <Kutu padded={false}>
            <div className="scroll-thin flex gap-1 overflow-x-auto border-b border-line px-2 pt-2">
              {sekmeler.map(([k, ad, n]) => (
                <button key={k} type="button" onClick={() => setSekme(k)} className={`whitespace-nowrap rounded-t-md px-3 py-2 text-sm font-medium transition ${sekme === k ? "border-b-2 border-orange text-ink" : "text-ink-3 hover:text-ink-2"}`}>{ad} <span className="tnum text-ink-3">{n}</span></button>
              ))}
            </div>

            {sekme === "agac" && (() => {
              const ustler = [...new Set(icerik.agac.map((a) => ustSection(a.section)))];
              const suzulen = icerik.agac.filter((a) =>
                (!agacSection || ustSection(a.section) === agacSection) &&
                gecer(agacAra, a.section, a.komponent, a.pn, a.kimlik, a.fmSatiri, a.notu, a.kaynakPiece));
              return (
              <>
              <FiltreCubugu gosterilen={suzulen.length} toplam={icerik.agac.length} temizle={() => { setAgacAra(""); setAgacSection(""); }}>
                <input className={girdi + " max-w-[280px]"} placeholder={cevir("component, PN, SN, note...")} value={agacAra} onChange={(e) => setAgacAra(e.target.value)} />
                <select className={girdi + " max-w-[200px]"} value={agacSection} onChange={(e) => setAgacSection(e.target.value)}>
                  <option value="">{cevir("All sections (")}{ustler.length})</option>
                  {ustler.map((u) => <option key={u} value={u}>Section {u}</option>)}
                </select>
              </FiltreCubugu>
              <div className="scroll-thin overflow-x-auto">
                <table className="w-full min-w-[860px]">
                  <thead><tr className="border-b border-line"><th className={tabloBas}>Section</th><th className={tabloBas}>{cevir("Component")}</th><th className={tabloBas}>PN</th><th className={tabloBas}>{cevir("SN / Heat / Other ID")}</th><th className={tabloBas}>FM-0011</th><th className={tabloBas}>{cevir("Source")}</th><th className={tabloBas}>{cevir("Note")}</th></tr></thead>
                  <tbody>{suzulen.map((a) => (
                    <tr key={a.id} className="border-b border-line last:border-0 align-top">
                      <td className={tabloHucre}><span className="font-mono text-xs text-ink-3">{a.section}</span></td>
                      <td className={tabloHucre} style={{ paddingLeft: 12 + a.derinlik * 18 }}><b className={a.derinlik === 0 ? "" : "font-medium"}>{a.komponent}</b></td>
                      <td className={tabloHucre}><span className="font-mono text-xs">{a.pn ?? "—"}</span></td>
                      <td className={tabloHucre}>{kimlikYok(a.kimlik) ? <Etiket>{cevir("No OEM record")}</Etiket> : <span className="font-mono text-xs">{a.kimlik ?? "—"}</span>}</td>
                      <td className={tabloHucre}><span className="text-xs text-ink-2">{a.fmSatiri ?? "—"}</span></td>
                      <td className={tabloHucre}>{kaynakSyf(a.kaynakPiece)}</td>
                      <td className={tabloHucre}><span className={`text-xs ${celiskiNotu(a.notu) ? "font-medium text-bad" : "text-ink-2"}`}>{a.notu ?? ""}</span></td>
                    </tr>
                  ))}
                    {!suzulen.length && <tr><td colSpan={7}><Bos>{!icerik.agac.length ? cevir("The component tree is not produced in fast mode. The Disassembly check uses the Identity and C&K map.") : cevir("No rows with this filter.")}</Bos></td></tr>}</tbody>
                </table>
              </div>
              </>
              );
            })()}

            {sekme === "piece" && (() => {
              const kapsamlar = [...new Set(icerik.pieceler.map((p) => p.kapsam).filter(Boolean))] as string[];
              const suzulen = icerik.pieceler.filter((p) =>
                (!pieceKapsam || p.kapsam === pieceKapsam) &&
                gecer(pieceAra, p.kod, p.sayfa, p.ad, p.duzenleyen, p.kapsamBeyani, p.komponent, p.sinif));
              return (
              <>
              <FiltreCubugu gosterilen={suzulen.length} toplam={icerik.pieceler.length} temizle={() => { setPieceAra(""); setPieceKapsam(""); }}>
                <input className={girdi + " max-w-[280px]"} placeholder={cevir("document, edited by, Identity...")} value={pieceAra} onChange={(e) => setPieceAra(e.target.value)} />
                <select className={girdi + " max-w-[200px]"} value={pieceKapsam} onChange={(e) => setPieceKapsam(e.target.value)}>
                  <option value="">{cevir("All scopes")}</option>
                  {kapsamlar.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </FiltreCubugu>
              <div className="scroll-thin overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead><tr className="border-b border-line"><th className={tabloBas}>Piece</th><th className={tabloBas}>{cevir("Page")}</th><th className={tabloBas}>{cevir("Minor document")}</th><th className={tabloBas}>{cevir("Edited by")}</th><th className={tabloBas}>{cevir("Class")}</th><th className={tabloBas}>{cevir("Scope")}</th><th className={tabloBas}>{cevir("Component")}</th></tr></thead>
                  <tbody>{suzulen.map((p) => (
                    <tr key={p.id} className="border-b border-line last:border-0 align-top">
                      <td className={tabloHucre}><b className="font-mono text-xs">{p.kod}</b></td>
                      <td className={tabloHucre}>{syf(p.sayfa)}</td>
                      <td className={tabloHucre}>{p.ad}{p.kapsamBeyani && <div className="mt-0.5 text-xs italic text-ink-3">"{p.kapsamBeyani}"</div>}</td>
                      <td className={tabloHucre}><span className="text-xs">{p.duzenleyen ?? "—"}</span></td>
                      <td className={tabloHucre}>{p.sinif ? <Etiket>{p.sinif}</Etiket> : "—"}</td>
                      <td className={tabloHucre}>{p.kapsam === "UNIQ" ? <Etiket tone="ok">UNIQ</Etiket> : p.kapsam === "GENERAL" ? <Etiket tone="info">GENERAL</Etiket> : <span className="text-xs">{p.kapsam ?? "—"}</span>}</td>
                      <td className={tabloHucre}><span className="text-xs">{p.komponent ?? "—"}</span></td>
                    </tr>
                  ))}
                    {!suzulen.length && <tr><td colSpan={7}><Bos>{cevir("No rows with this filter.")}</Bos></td></tr>}</tbody>
                </table>
              </div>
              </>
              );
            })()}

            {sekme === "kimlik" && (() => {
              const komponentler = [...new Set(icerik.kimlikler.map((k) => k.komponent))];
              const eslesmeSayisi = icerik.kimlikler.filter((k) => k.eslesme).length;
              const suzulen = icerik.kimlikler.filter((k) =>
                (!kimlikKomponent || k.komponent === kimlikKomponent) &&
                (!sadeceEslesme || k.eslesme) &&
                gecer(kimlikAra, k.komponent, k.alan, k.deger, k.rev, k.kaynak, k.sinif, k.diger));
              return (
              <>
              <FiltreCubugu gosterilen={suzulen.length} toplam={icerik.kimlikler.length} temizle={() => { setKimlikAra(""); setKimlikKomponent(""); setSadeceEslesme(false); }}>
                <input className={girdi + " max-w-[280px]"} placeholder={cevir("component, field, value...")} value={kimlikAra} onChange={(e) => setKimlikAra(e.target.value)} />
                <select className={girdi + " max-w-[200px]"} value={kimlikKomponent} onChange={(e) => setKimlikKomponent(e.target.value)}>
                  <option value="">{cevir("All components")}</option>
                  {komponentler.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-sm text-ink-2">
                  <input type="checkbox" checked={sadeceEslesme} onChange={(e) => setSadeceEslesme(e.target.checked)} />
                  {cevir`only match keys (${eslesmeSayisi})`}
                </label>
              </FiltreCubugu>
              <div className="scroll-thin overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead><tr className="border-b border-line"><th className={tabloBas}>{cevir("Component")}</th><th className={tabloBas}>{cevir("Field")}</th><th className={tabloBas}>{cevir("Canonical value")}</th><th className={tabloBas}>Rev</th><th className={tabloBas}>{cevir("Source")}</th><th className={tabloBas}>{cevir("Class")}</th><th className={tabloBas}>{cevir("Support")}</th><th className={tabloBas}>{cevir("Match key")}</th><th className={tabloBas}>{cevir("Other values in MRB")}</th></tr></thead>
                  <tbody>{suzulen.map((k) => (
                    <tr key={k.id} className="border-b border-line last:border-0 align-top">
                      <td className={tabloHucre}><b className="text-xs">{k.komponent}</b></td>
                      <td className={tabloHucre}><span className="text-xs">{k.alan}</span></td>
                      <td className={tabloHucre}><b className="font-mono text-xs">{k.deger ?? "—"}</b></td>
                      <td className={tabloHucre}><span className="font-mono text-xs">{k.rev ?? "—"}</span></td>
                      <td className={tabloHucre}>{kaynakBelge(k.kaynak)}</td>
                      <td className={tabloHucre}>{k.sinif ? <Etiket>{k.sinif}</Etiket> : "—"}</td>
                      <td className={tabloHucre}><span className="tnum text-xs" title={cevir("occurrences carrying the canonical value / all occurrences of the field")}>{k.destek ?? "—"} / {k.toplam ?? "—"}</span></td>
                      <td className={tabloHucre}>{k.eslesme && <Etiket tone="ok">{cevir("Match")}</Etiket>}</td>
                      <td className={tabloHucre}><span className="text-xs text-ink-2">{k.diger ?? "—"}</span></td>
                    </tr>
                  ))}
                    {!suzulen.length && <tr><td colSpan={9}><Bos>{cevir("No rows with this filter.")}</Bos></td></tr>}</tbody>
                </table>
              </div>
              </>
              );
            })()}

            {sekme === "karar" && (
              <div className="scroll-thin overflow-x-auto">
                <table className="w-full min-w-[860px]">
                  <thead><tr className="border-b border-line"><th className={tabloBas}>#</th><th className={tabloBas}>{cevir("Component")}</th><th className={tabloBas}>{cevir("Field")}</th><th className={tabloBas}>{cevir("Line")}</th><th className={tabloBas}>{cevir("Issue type")}</th><th className={tabloBas}>{cevir("Canonical (proposed)")}</th><th className={tabloBas}>{cevir("Deviating values")}</th></tr></thead>
                  <tbody>{icerik.kararlar.map((k) => (
                    <tr key={k.id} className="border-b border-line last:border-0 align-top">
                      <td className={tabloHucre}><span className="font-mono text-xs text-ink-3">{k.no ?? k.sira}</span></td>
                      <td className={tabloHucre}><b className="text-xs">{k.komponent}</b></td>
                      <td className={tabloHucre}><span className="text-xs">{k.alan ?? "—"}</span></td>
                      <td className={tabloHucre}><span className="font-mono text-xs">{k.hat ?? "—"}</span></td>
                      <td className={tabloHucre}><Etiket tone={k.tur === "REV_UNCONFIRMED" ? "warn" : "bad"}>{k.tur}</Etiket></td>
                      <td className={tabloHucre}><b className="font-mono text-xs">{k.kanonik ?? "—"}</b></td>
                      <td className={tabloHucre}><span className="text-xs text-ink-2">{k.detay ?? "—"}</span></td>
                    </tr>
                  ))}
                    {!icerik.kararlar.length && <tr><td colSpan={7}><Bos>{cevir("No conflicts flagged by the converter.")}</Bos></td></tr>}</tbody>
                </table>
              </div>
            )}

            {sekme === "ck" && (() => {
              const hatlar = [...new Set(icerik.ckSatirlari.map((c) => c.hat))];
              const suzulen = icerik.ckSatirlari.filter((c) => (!ckHat || c.hat === ckHat) && gecer(ckAra, c.hat, c.komponent, c.alan, c.deger, c.kaynak, c.fizikiHat, c.diger));
              return (
              <>
              <FiltreCubugu gosterilen={suzulen.length} toplam={icerik.ckSatirlari.length} temizle={() => { setCkAra(""); setCkHat(""); }}>
                <input className={girdi + " max-w-[280px]"} placeholder={cevir("component, field, value...")} value={ckAra} onChange={(e) => setCkAra(e.target.value)} />
                <select className={girdi + " max-w-[200px]"} value={ckHat} onChange={(e) => setCkHat(e.target.value)}>
                  <option value="">{cevir("Both lines")}</option>
                  {hatlar.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </FiltreCubugu>
              <div className="scroll-thin overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead><tr className="border-b border-line"><th className={tabloBas}>{cevir("Line")}</th><th className={tabloBas}>{cevir("Component")}</th><th className={tabloBas}>{cevir("Field")}</th><th className={tabloBas}>{cevir("Canonical value")}</th><th className={tabloBas}>Rev</th><th className={tabloBas}>{cevir("Source")}</th><th className={tabloBas}>{cevir("Support")}</th><th className={tabloBas}>{cevir("Physical line")}</th><th className={tabloBas}>{cevir("Other values in MRB")}</th></tr></thead>
                  <tbody>{suzulen.map((c) => (
                    <tr key={c.id} className="border-b border-line last:border-0 align-top">
                      <td className={tabloHucre}><b className="font-mono text-xs">{c.hat}</b></td>
                      <td className={tabloHucre}><span className="text-xs">{c.komponent}</span></td>
                      <td className={tabloHucre}><span className="text-xs">{c.alan}</span></td>
                      <td className={tabloHucre}><b className="font-mono text-xs">{c.deger ?? "—"}</b></td>
                      <td className={tabloHucre}><span className="font-mono text-xs">{c.rev ?? "—"}</span></td>
                      <td className={tabloHucre}>{kaynakBelge(c.kaynak)}</td>
                      <td className={tabloHucre}><span className="tnum text-xs">{c.destek ?? "—"}</span></td>
                      <td className={tabloHucre}>{c.fizikiHat ? <Etiket tone="ok">{c.fizikiHat}</Etiket> : <span className="text-xs text-ink-3">{cevir("at disassembly")}</span>}</td>
                      <td className={tabloHucre}><span className="text-xs text-ink-2">{c.diger ?? "—"}</span></td>
                    </tr>
                  ))}
                    {!suzulen.length && <tr><td colSpan={9}><Bos>{cevir("No rows with this filter.")}</Bos></td></tr>}</tbody>
                </table>
              </div>
              </>
              );
            })()}

          </Kutu>

          <Band tip="bilgi">{cevir("Identity and C&K map hold one canonical value per field, chosen by the reference hierarchy (BOM > Trace sheet > CoC > TOC). Rows marked Match key are the identities the Disassembly old ID check compares against (the component tree too, if the MRB has one); Other values in MRB are shown as evidence when an ID does not match. The physical Choke / Kill line of 2A and 2B is filled at disassembly.")}</Band>
        </>
      )}

      {veri.olaylar.length > 0 && (
        <Kutu baslik={cevir("Record history")}>
          <div className="border-l-2 border-line-strong pl-3 text-sm text-ink-2">{veri.olaylar.map((o) => <OlayDuzenle key={o.id} o={o} onDegisti={yenile}>• {tarihSaat(o.ts)} · {o.kim} · {o.olay}</OlayDuzenle>)}</div>
        </Kutu>
      )}
      {pdf && mrb.pdfDokumanId && (
        <Suspense fallback={null}>
          <SurvrepGoruntuleyici dokumanId={mrb.pdfDokumanId} ad={mrb.pdfAd ?? mrb.dosya} sayfa={pdf.sayfa} onKapat={() => setPdf(null)} />
        </Suspense>
      )}
    </Sayfa>
  );
}
